import type { HTMLAttributes } from 'react';
import { tv } from 'tailwind-variants';

import type { Task } from '@/project-management/entities/task';
import { TaskPriority } from '@/project-management/entities/task-priority';
import { TaskStatus } from '@/project-management/entities/task-status';

const taskBadgeVariants = tv({
  base: 'size-4 cursor-default rounded-full',
  variants: {
    priority: {
      [TaskPriority.LOW]: 'ring-teal-500',
      [TaskPriority.MEDIUM]: 'ring-yellow-500',
      [TaskPriority.HIGH]: 'ring-red-500',
    },
    completed: {
      true: '',
      false: 'border-2',
    },
    clickable: {
      true: 'cursor-pointer hover:ring-2',
      false: '',
    },
  },
  compoundVariants: [
    {
      priority: TaskPriority.LOW,
      completed: true,
      className: 'bg-teal-500',
    },
    {
      priority: TaskPriority.LOW,
      completed: false,
      className: 'border-teal-500 bg-teal-200',
    },
    {
      priority: TaskPriority.MEDIUM,
      completed: true,
      className: 'bg-yellow-500',
    },
    {
      priority: TaskPriority.MEDIUM,
      completed: false,
      className: 'border-yellow-500 bg-yellow-200',
    },
    {
      priority: TaskPriority.HIGH,
      completed: true,
      className: 'bg-red-500',
    },
    {
      priority: TaskPriority.HIGH,
      completed: false,
      className: 'border-red-500 bg-red-200',
    },
  ],
});

export type TaskBadgeProps = HTMLAttributes<HTMLDivElement> & {
  task: Pick<Task, 'priority' | 'status'>;
  clickable?: boolean;
};

export function TaskBadge({
  task,
  className,
  clickable,
  ...props
}: TaskBadgeProps) {
  const style = taskBadgeVariants({
    className,
    priority: task.priority,
    completed: task.status === TaskStatus.DONE,
    clickable,
  });

  return <div className={style} {...props} />;
}
